/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 *
 * Modal acceso externo contador/auditor. Montado solo si EXTERNAL_ORG_ACCESS_UI_ENABLED.
 * Ver organizationExternalAccessFeature.ts.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography'
import { colors } from '../../../shared/theme/colors';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { showError, showSuccess } from '../../../shared/ui/appToast';
import {
  grantOrganizationAccess,
  listOrganizationAccessGrants,
  revokeOrganizationAccess,
  type OrganizationAccessGrant,
  type OrganizationAccessGrantRole,
} from '../organizationAccessGrantsService';

type Props = {
  visible: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
};

const ROLES: { value: OrganizationAccessGrantRole; label: string }[] = [
  { value: 'contador', label: 'Contador' },
  { value: 'auditor', label: 'Auditor' },
];

export function OrganizationAccessGrantsSheet({
  visible,
  onClose,
  organizationId,
  organizationName,
}: Props) {
  const insets = useSafeAreaInsets();
  const { width, height: screenHeight } = useWindowDimensions();
  const isDesktop = width >= 768;
  const sheetMaxHeight = screenHeight * 0.9;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [grants, setGrants] = useState<OrganizationAccessGrant[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrganizationAccessGrantRole>('contador');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listOrganizationAccessGrants(organizationId);
      setGrants(rows);
    } catch (e) {
      presentUnexpectedFailure({ module: 'shell', op: 'no-pudimos-cargar-los-accesos', error: e, title: 'No pudimos cargar los accesos', userMessage: 'Reintentá.' });
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (visible) {
      setEmail('');
      setRole('contador');
      void load();
    }
  }, [visible, load]);

  const handleGrant = async () => {
    const trimmed = email.trim();
    if (!trimmed.includes('@')) {
      showError('Email inválido', 'Escribí el mail de la cuenta que ya usa Vuelto.');
      return;
    }
    setSaving(true);
    try {
      await grantOrganizationAccess({
        organizationId,
        email: trimmed,
        role,
      });
      showSuccess('Acceso dado', `${role === 'contador' ? 'Contador' : 'Auditor'}: ${trimmed}`);
      setEmail('');
      await load();
    } catch (e) {
      presentUnexpectedFailure({ module: 'shell', op: 'no-se-pudo-dar-acceso', error: e, title: 'No se pudo dar acceso', userMessage: 'Reintentá.' });
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (grant: OrganizationAccessGrant) => {
    setSaving(true);
    try {
      await revokeOrganizationAccess(grant.id);
      showSuccess('Acceso quitado', grant.grantee_email ?? 'Listo.');
      await load();
    } catch (e) {
      presentUnexpectedFailure({ module: 'shell', op: 'no-se-pudo-quitar', error: e, title: 'No se pudo quitar', userMessage: 'Reintentá.' });
    } finally {
      setSaving(false);
    }
  };

  const sheetContent = (
    <View style={styles.sheetInner} testID="org-grants.sheet">
      <View style={styles.grab} />
      <View style={styles.header}>
        <Text style={styles.title}>Acceso externo</Text>
        <TouchableOpacity onPress={onClose} accessibilityRole="button">
          <Text style={styles.close}>Listo</Text>
        </TouchableOpacity>
      </View>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator
        contentContainerStyle={styles.scrollContent}
      >
        <Text style={styles.hint}>
          Contador o auditor ven{' '}
          <Text style={styles.hintStrong}>{organizationName}</Text> en solo lectura. La cuenta tiene que existir
          en Vuelto.
        </Text>

        <Text style={styles.label}>Email</Text>
        <TextInput
          style={styles.input}
          value={email}
          onChangeText={setEmail}
          autoCapitalize="none"
          keyboardType="email-address"
          placeholder="contador@estudio.com"
          placeholderTextColor={colors.textSecondary}
          testID="org-grants.email"
        />

        <Text style={styles.label}>Rol</Text>
        <View style={styles.roleRow}>
          {ROLES.map((r) => (
            <TouchableOpacity
              key={r.value}
              style={[styles.roleChip, role === r.value && styles.roleChipActive]}
              onPress={() => setRole(r.value)}
              accessibilityRole="button"
              testID={`org-grants.role.${r.value}`}
            >
              <Text style={[styles.roleChipText, role === r.value && styles.roleChipTextActive]}>{r.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        <TouchableOpacity
          style={[styles.primary, saving && styles.primaryDisabled]}
          onPress={() => void handleGrant()}
          disabled={saving}
          accessibilityRole="button"
          testID="org-grants.submit"
        >
          {saving ? (
            <ActivityIndicator color={ticketColors.paper} />
          ) : (
            <Text style={styles.primaryText}>Dar acceso</Text>
          )}
        </TouchableOpacity>

        <Text style={styles.section}>Ya tienen acceso</Text>
        {loading ? <ActivityIndicator color={ticketColors.gold} style={styles.loader} /> : null}
        {grants.length === 0 && !loading ? (
          <Text style={styles.empty}>Todavía no hay contador ni auditor.</Text>
        ) : null}
        {grants.map((g) => (
          <View key={g.id} style={styles.row} testID={`org-grants.row.${g.id}`}>
            <View style={styles.rowText}>
              <Text style={styles.rowEmail} numberOfLines={2}>
                {g.grantee_email ?? 'Sin email'}
              </Text>
              <Text style={styles.rowMeta}>{g.role === 'contador' ? 'Contador' : 'Auditor'}</Text>
            </View>
            <TouchableOpacity
              onPress={() => void handleRevoke(g)}
              disabled={saving}
              accessibilityRole="button"
              testID={`org-grants.revoke.${g.id}`}
              style={styles.revokeBtn}
            >
              <Text style={styles.revoke}>Quitar</Text>
            </TouchableOpacity>
          </View>
        ))}
      </ScrollView>
    </View>
  );

  if (isDesktop) {
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
        <View style={[styles.backdrop, { justifyContent: 'center', alignItems: 'center' }]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" accessibilityLabel="Cerrar" />
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            style={[
              styles.sheet,
              { paddingBottom: spacing.lg, maxHeight: sheetMaxHeight, maxWidth: 520, borderRadius: 16 },
            ]}
          >
            {sheetContent}
          </KeyboardAvoidingView>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" accessibilityLabel="Cerrar" />
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={[
            styles.sheet,
            { paddingBottom: Math.max(insets.bottom, spacing.lg), maxHeight: sheetMaxHeight },
          ]}
        >
          {sheetContent}
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(28, 25, 23, 0.35)',
    justifyContent: 'flex-end',
  },
  sheet: {
    width: '100%',
  },
  sheetInner: {
    backgroundColor: colors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: spacing.lg,
    overflow: 'hidden',
  },
  scrollContent: {
    paddingBottom: spacing.sm,
  },
  grab: {
    width: 40,
    height: 4,
    borderRadius: 999,
    backgroundColor: 'rgba(28, 25, 23, 0.15)',
    alignSelf: 'center',
    marginTop: spacing.sm,
    marginBottom: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  title: {
    ...typography.title3,
    color: colors.textPrimary,
  },
  close: {
    ...typography.body,
    fontWeight: '700',
    color: ticketColors.gold,
  },
  hint: {
    ...typography.body,
    fontSize: 14,
    lineHeight: 21,
    color: colors.textSecondary,
    marginBottom: spacing.lg,
  },
  hintStrong: {
    fontWeight: '700',
    color: colors.textPrimary,
  },
  label: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  input: {
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    ...typography.body,
    color: colors.textPrimary,
    backgroundColor: ticketColors.paper,
    marginBottom: spacing.sm,
  },
  roleRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  roleChip: {
    flex: 1,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    backgroundColor: ticketColors.paper,
  },
  roleChipActive: {
    borderColor: ticketColors.gold,
    backgroundColor: 'rgba(180, 83, 9, 0.08)',
  },
  roleChipText: {
    ...typography.body,
    color: colors.textSecondary,
    fontWeight: '600',
  },
  roleChipTextActive: {
    color: ticketColors.gold,
  },
  primary: {
    backgroundColor: ticketColors.gold,
    borderRadius: 14,
    paddingVertical: spacing.md,
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  primaryDisabled: { opacity: 0.7 },
  primaryText: {
    ...typography.body,
    fontWeight: '700',
    color: ticketColors.paper,
  },
  section: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textSecondary,
    marginBottom: spacing.sm,
    textTransform: 'uppercase',
  },
  loader: { marginVertical: spacing.md },
  empty: {
    ...typography.caption,
    color: colors.textSecondary,
    marginBottom: spacing.md,
    lineHeight: 18,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    padding: spacing.md,
    marginBottom: spacing.sm,
    backgroundColor: ticketColors.paper,
  },
  rowText: { flex: 1, minWidth: 0 },
  rowEmail: {
    ...typography.body,
    fontWeight: '600',
    color: colors.textPrimary,
    flexShrink: 1,
  },
  rowMeta: {
    ...typography.caption,
    color: colors.textSecondary,
    marginTop: 2,
    lineHeight: 18,
  },
  revokeBtn: {
    flexShrink: 0,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.xs,
  },
  revoke: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.danger,
  },
});
